import { Box, Tab, Tabs } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { useCallback } from 'react';
import StudentUpcomingSessionTable from './StudentUpcomingSessionTable/StudentUpcomingSessionTable';
import StudentCompletedSessionTable from './StudentCompletedSessionTable/StudentCompletedSessionTable';
import StudentCancelledSessionTable from './StudentCancelledSessionTable/StudentCancelledSessionTable';
import { TableSkeleton } from '../../../../../components/Admin/SkeletonLoader/SkeletonLoader';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      aria-label={`${props.label} panel`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const StudentSession = () => {
  const [value, setValue] = useState(0);
  const [upcomingSessionData, setUpcomingSessionData] = useState([]);
  const [completedSessionData, setCompletedSessionData] = useState([]);
  const [cancelledSessionData, setCancelledSessionData] = useState([]);
  const [upcomingPagination, setUpcomingPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
  });
  const [completedPagination, setCompletedPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
  });
  const [cancelledPagination, setCancelledPagination] = useState({
    page: 1,
    page_size: 10,
    total_count: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { id } = useParams();
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [loadingCancelled, setLoadingCancelled] = useState(false);
  const [hasLoadedUpcoming, setHasLoadedUpcoming] = useState(false);
  const [hasLoadedCompleted, setHasLoadedCompleted] = useState(false);
  const [hasLoadedCancelled, setHasLoadedCancelled] = useState(false);

  const getUpcomingSessionData = useCallback(
    async (page = 1, pageSize = 10, search = '') => {
      try {
        setLoadingUpcoming(true);
        let url = `/admin/students/${id}/sessions?status=upcoming&page=${page}&page_size=${pageSize}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        const { data } = await axiosInstance.get(url);
        setUpcomingSessionData(data.results || []);
        setUpcomingPagination({
          page: page,
          page_size: pageSize,
          total_count: data.count || 0,
        });
        setHasLoadedUpcoming(true);
      } catch (error) {
        console.log(error);
        setUpcomingSessionData([]);
        setUpcomingPagination({
          page: 1,
          page_size: 10,
          total_count: 0,
        });
        setHasLoadedUpcoming(true);
      } finally {
        setLoadingUpcoming(false);
      }
    },
    [id]
  );

  const getCompletedSessionData = useCallback(
    async (page = 1, pageSize = 10, search = '') => {
      try {
        setLoadingCompleted(true);
        let url = `/admin/students/${id}/sessions?status=completed&page=${page}&page_size=${pageSize}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        console.log('Completed API call:', url);
        const { data } = await axiosInstance.get(url);
        console.log('Completed API response:', data);
        setCompletedSessionData(data.results || []);
        setCompletedPagination({
          page: page,
          page_size: pageSize,
          total_count: data.count || 0,
        });
        console.log('Completed pagination set:', { page, pageSize, total_count: data.count || 0 });
        setHasLoadedCompleted(true);
      } catch (error) {
        console.log('Completed sessions error:', error);
        setCompletedSessionData([]);
        setCompletedPagination({
          page: 1,
          page_size: 10,
          total_count: 0,
        });
        setHasLoadedCompleted(true);
      } finally {
        setLoadingCompleted(false);
      }
    },
    [id]
  );

  const getCancelledSessionData = useCallback(
    async (page = 1, pageSize = 10, search = '') => {
      try {
        setLoadingCancelled(true);
        let url = `/admin/students/${id}/sessions?status=cancelled&page=${page}&page_size=${pageSize}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        console.log('Cancelled API call:', url);
        const { data } = await axiosInstance.get(url);
        console.log('Cancelled API response:', data);
        setCancelledSessionData(data.results || []);
        setCancelledPagination({
          page: page,
          page_size: pageSize,
          total_count: data.count || 0,
        });
        console.log('Cancelled pagination set:', { page, pageSize, total_count: data.count || 0 });
        setHasLoadedCancelled(true);
      } catch (error) {
        console.log('Cancelled sessions error:', error);
        setCancelledSessionData([]);
        setCancelledPagination({
          page: 1,
          page_size: 10,
          total_count: 0,
        });
        setHasLoadedCancelled(true);
      } finally {
        setLoadingCancelled(false);
      }
    },
    [id]
  );

  const handleUpcomingPaginationChange = useCallback(
    (newPage, newPageSize) => {
      getUpcomingSessionData(newPage, newPageSize, debouncedSearchTerm);
    },
    [getUpcomingSessionData, debouncedSearchTerm]
  );

  const handleCompletedPaginationChange = useCallback(
    (newPage, newPageSize) => {
      console.log('Completed pagination handler called:', { newPage, newPageSize });
      getCompletedSessionData(newPage, newPageSize, '');
    },
    [getCompletedSessionData]
  );

  const handleCancelledPaginationChange = useCallback(
    (newPage, newPageSize) => {
      console.log('Cancelled pagination handler called:', { newPage, newPageSize });
      getCancelledSessionData(newPage, newPageSize, '');
    },
    [getCancelledSessionData]
  );

  const handleSearchChange = useCallback(newSearchTerm => {
    setSearchTerm(newSearchTerm);
  }, []);

  // Effect to trigger search when debounced value changes
  useEffect(() => {
    if (value === 0) {
      getUpcomingSessionData(1, upcomingPagination.page_size, debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, getUpcomingSessionData, upcomingPagination.page_size, value]);

  useEffect(() => {
    if (id && value === 0 && !hasLoadedUpcoming) {
      console.log('Initial load - calling upcoming session API');
      getUpcomingSessionData(1, 10, '');
    }
  }, [id, value, hasLoadedUpcoming, getUpcomingSessionData]);

  useEffect(() => {
    if (value === 0 && !hasLoadedUpcoming) {
      getUpcomingSessionData(1, 10, '');
    } else if (value === 1 && !hasLoadedCompleted) {
      getCompletedSessionData(1, 10, '');
    } else if (value === 2 && !hasLoadedCancelled) {
      getCancelledSessionData(1, 10, '');
    }
  }, [
    value,
    hasLoadedUpcoming,
    hasLoadedCompleted,
    hasLoadedCancelled,
    getUpcomingSessionData,
    getCompletedSessionData,
    getCancelledSessionData,
  ]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  return (
    <Box
      data-testid="teacher-session-component"
      className="h-[calc(100vh-260px)] overflow-auto relative bg-[#F9F9F9] rounded-lg"
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="Upcoming" sx={{ textTransform: 'none' }} {...a11yProps(0)} />
            <Tab label="Completed" sx={{ textTransform: 'none' }} {...a11yProps(1)} />
            <Tab label="Cancelled" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          {loadingUpcoming ? (
            <TableSkeleton />
          ) : (
            <StudentUpcomingSessionTable
              upcomingSessionData={upcomingSessionData}
              pagination={upcomingPagination}
              onPaginationChange={handleUpcomingPaginationChange}
              onSearchChange={handleSearchChange}
            />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {loadingCompleted ? (
            <TableSkeleton />
          ) : (
            <StudentCompletedSessionTable
              completedSessionData={completedSessionData}
              pagination={completedPagination}
              onPaginationChange={handleCompletedPaginationChange}
              onSearchChange={handleSearchChange}
            />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {loadingCancelled ? (
            <TableSkeleton />
          ) : (
            <StudentCancelledSessionTable
              cancelledSessionData={cancelledSessionData}
              pagination={cancelledPagination}
              onPaginationChange={handleCancelledPaginationChange}
              onSearchChange={handleSearchChange}
            />
          )}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default StudentSession;
